import { zodResolver } from '@hookform/resolvers/zod';
import { Card } from '@ipfabric/ui-components/Card/Card';
import type { CronRef } from '@ipfabric/ui-components/Cron/Cron';
import type { CronFormMeta } from '@ipfabric/ui-components/Cron/cronTypes';
import { FormCron } from '@ipfabric/ui-components/FormCron/FormCron';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { LeaveDialog } from '@ipfabric/ui-components/LeaveDialog/LeaveDialog';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import {
  MAX_LOADED_SNAPSHOTS_FROM,
  MAX_LOADED_SNAPSHOTS_TO,
  MAX_LOCKED_SNAPSHOTS_FROM,
} from '@ipfabric/utils';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type Control, FormProvider, useWatch } from 'react-hook-form';
import z from 'zod';
import type { snapshotCollectionMessages } from './snapshotCollectionMessages';
import { prepareSnapshotCollectionValues } from './snapshotCollectionModel';
import type { SnapshotCollectionValues } from './snapshotCollectionModel';

type SnapshotCollectionMessages = typeof snapshotCollectionMessages.en;

/**
 * Form values type with string backing for number inputs.
 * HTML inputs always return strings, so form state should reflect that.
 * Zod coerces strings to numbers during validation/submission.
 */
export type SnapshotCollectionFormValues = {
  snapshots: {
    enabled: boolean;
    cron: { expression: string; timezone: string };
    maxLoaded: string;
    maxLocked: string;
  };
  snapshotsRetention: {
    all: { enabled: boolean; days: string };
    firstOfDay: { enabled: boolean; days: string };
    firstOfWeek: { enabled: boolean; weeks: string };
    firstOfMonth: { enabled: boolean; months: string };
    firstOfYear: { enabled: boolean; years: string };
    removalMetrics: { hddUtilization: string; snapshotsCount: string };
  };
};

type SnapshotCollectionFormProps = {
  initialValues: SnapshotCollectionValues;
  messages: SnapshotCollectionMessages;
  onSubmit: (values: SnapshotCollectionValues) => void;
  submitting?: boolean;
  onFormStateChange?: (state: { isDirty: boolean; cronValid: boolean }) => void;
};

export const snapshotCollectionFormId = 'snapshotCollectionForm';

const mapInitialValues = (
  values: SnapshotCollectionValues,
): SnapshotCollectionFormValues => ({
  snapshots: {
    enabled: values.snapshots.enabled,
    cron: { ...values.snapshots.cron },
    maxLoaded: String(values.snapshots.maxLoaded),
    maxLocked: String(values.snapshots.maxLocked),
  },
  snapshotsRetention: {
    all: {
      enabled: values.snapshotsRetention.all.enabled,
      days: String(values.snapshotsRetention.all.days),
    },
    firstOfDay: {
      enabled: values.snapshotsRetention.firstOfDay.enabled,
      days: String(values.snapshotsRetention.firstOfDay.days),
    },
    firstOfWeek: {
      enabled: values.snapshotsRetention.firstOfWeek.enabled,
      weeks: String(values.snapshotsRetention.firstOfWeek.weeks),
    },
    firstOfMonth: {
      enabled: values.snapshotsRetention.firstOfMonth.enabled,
      months: String(values.snapshotsRetention.firstOfMonth.months),
    },
    firstOfYear: {
      enabled: values.snapshotsRetention.firstOfYear.enabled,
      years: String(values.snapshotsRetention.firstOfYear.years),
    },
    removalMetrics: {
      hddUtilization: String(
        values.snapshotsRetention.removalMetrics.hddUtilization,
      ),
      snapshotsCount: String(
        values.snapshotsRetention.removalMetrics.snapshotsCount,
      ),
    },
  },
});

const createValidationSchema = (messages: SnapshotCollectionMessages) => {
  const requiredMessage = messages.validation.required;
  const cronSchema = z.object({
    expression: z.string().min(1, requiredMessage),
    timezone: z.string().min(1, requiredMessage),
  });

  const integerMessage = 'Value must be an integer.';
  const createPositiveNumberSchema = (
    message: string,
    upperBound?: number,
    rangeMessage?: string,
  ) => {
    const schema = z.coerce
      .number({ invalid_type_error: 'Value must be a number.' })
      .safe()
      .int(integerMessage)
      .min(1, message);
    if (upperBound !== undefined) {
      return schema.max(upperBound, rangeMessage ?? message);
    }
    return schema;
  };
  const hddUtilizationRangeMessage = 'Value must be between 1 and 90.';
  const minNumberMessage = 'Value must be greater than 0.';

  const snapshotsCommonSchema = z.object({
    maxLoaded: z.coerce
      .number({ invalid_type_error: requiredMessage })
      .safe()
      .int(integerMessage)
      .min(MAX_LOADED_SNAPSHOTS_FROM, messages.validation.maxLoadedRange)
      .max(MAX_LOADED_SNAPSHOTS_TO, messages.validation.maxLoadedRange),
    maxLocked: z.coerce
      .number({ invalid_type_error: requiredMessage })
      .safe()
      .int(integerMessage)
      .min(MAX_LOCKED_SNAPSHOTS_FROM, messages.validation.maxLockedRange),
  });

  const snapshotsSchema = z
    .discriminatedUnion('enabled', [
      z
        .object({
          enabled: z.literal(true),
          cron: cronSchema,
        })
        .merge(snapshotsCommonSchema),
      z
        .object({
          enabled: z.literal(false),
          cron: z.object({
            expression: z.string(),
            timezone: z.string(),
          }),
        })
        .merge(snapshotsCommonSchema),
    ])
    .refine((data) => data.maxLocked < data.maxLoaded, {
      path: ['maxLocked'],
      message: messages.validation.maxLockedRange,
    });

  return z.object({
    snapshots: snapshotsSchema,
    snapshotsRetention: z.object({
      all: z.object({
        enabled: z.boolean(),
        days: createPositiveNumberSchema(minNumberMessage),
      }),
      firstOfDay: z.object({
        enabled: z.boolean(),
        days: createPositiveNumberSchema(minNumberMessage),
      }),
      firstOfWeek: z.object({
        enabled: z.boolean(),
        weeks: createPositiveNumberSchema(minNumberMessage),
      }),
      firstOfMonth: z.object({
        enabled: z.boolean(),
        months: createPositiveNumberSchema(minNumberMessage),
      }),
      firstOfYear: z.object({
        enabled: z.boolean(),
        years: createPositiveNumberSchema(minNumberMessage),
      }),
      removalMetrics: z.object({
        hddUtilization: createPositiveNumberSchema(
          hddUtilizationRangeMessage,
          90,
          hddUtilizationRangeMessage,
        ),
        snapshotsCount: createPositiveNumberSchema(minNumberMessage),
      }),
    }),
  });
};

type RetentionEnabledPath =
  | 'snapshotsRetention.all.enabled'
  | 'snapshotsRetention.firstOfDay.enabled'
  | 'snapshotsRetention.firstOfWeek.enabled'
  | 'snapshotsRetention.firstOfMonth.enabled'
  | 'snapshotsRetention.firstOfYear.enabled';

type RetentionValuePath =
  | 'snapshotsRetention.all.days'
  | 'snapshotsRetention.firstOfDay.days'
  | 'snapshotsRetention.firstOfWeek.weeks'
  | 'snapshotsRetention.firstOfMonth.months'
  | 'snapshotsRetention.firstOfYear.years';

const RetentionField = ({
  control,
  enabledName,
  valueName,
  label,
  unit,
  disabled = false,
}: {
  control: Control<SnapshotCollectionFormValues>;
  enabledName: RetentionEnabledPath;
  valueName: RetentionValuePath;
  label: ReactNode;
  unit: ReactNode;
  disabled?: boolean;
}) => {
  const isEnabled = useWatch({
    control,
    name: enabledName,
  });

  return (
    <div className='flex flex-col gap-2'>
      <FormSwitch<SnapshotCollectionFormValues>
        control={control}
        name={enabledName}
        label={label}
        disabled={disabled}
        className='w-fit'
      />

      <FormTextField<SnapshotCollectionFormValues>
        control={control}
        name={valueName}
        type='number'
        disabled={disabled || isEnabled !== true}
        endAdornment={unit}
        inputWidthClassName='w-60'
        className='ml-12'
        min={1}
        max={Number.MAX_SAFE_INTEGER}
      />
    </div>
  );
};

export const SnapshotCollectionForm = ({
  initialValues,
  onSubmit,
  messages,
  submitting = false,
  onFormStateChange,
}: SnapshotCollectionFormProps) => {
  const retentionAction = initialValues.snapshotsRetention.action;
  const formMethods = useCustomForm<SnapshotCollectionFormValues>({
    defaultValues: mapInitialValues(initialValues),
    resolver: zodResolver(createValidationSchema(messages)),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { isDirty },
  } = formMethods;

  const cronRef = useRef<CronRef>(null);
  const [cronValid, setCronValid] = useState(true);

  const handleCronMetaChange = useCallback((meta: CronFormMeta) => {
    setCronValid(meta.valid);
  }, []);

  const isSnapshotsEnabled = useWatch({
    control,
    name: 'snapshots.enabled',
  });
  const snapshotMaxLoaded = useWatch({
    control,
    name: 'snapshots.maxLoaded',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reaction intended to run on snapshotMaxLoaded change
  useEffect(() => {
    trigger('snapshots.maxLocked');
  }, [snapshotMaxLoaded, trigger]);

  const isCronDisabled = submitting || isSnapshotsEnabled !== true;

  useEffect(() => {
    reset(mapInitialValues(initialValues), {
      keepDirty: false,
    });
  }, [initialValues, reset]);

  const setValueOptions = { shouldDirty: true, shouldValidate: true } as const;

  const handleCollectionRulesResetToDefaults = useStableCallback(() => {
    const { snapshotsRetention, snapshots } = prepareSnapshotCollectionValues();
    const { all, firstOfDay, firstOfWeek, firstOfMonth, firstOfYear } =
      snapshotsRetention;

    setValue(
      'snapshots.maxLoaded',
      String(snapshots.maxLoaded),
      setValueOptions,
    );
    setValue(
      'snapshots.maxLocked',
      String(snapshots.maxLocked),
      setValueOptions,
    );

    setValue('snapshotsRetention.all.enabled', all.enabled, setValueOptions);
    setValue('snapshotsRetention.all.days', String(all.days), setValueOptions);
    setValue(
      'snapshotsRetention.firstOfDay.enabled',
      firstOfDay.enabled,
      setValueOptions,
    );
    setValue(
      'snapshotsRetention.firstOfDay.days',
      String(firstOfDay.days),
      setValueOptions,
    );
    setValue(
      'snapshotsRetention.firstOfWeek.enabled',
      firstOfWeek.enabled,
      setValueOptions,
    );
    setValue(
      'snapshotsRetention.firstOfWeek.weeks',
      String(firstOfWeek.weeks),
      setValueOptions,
    );
    setValue(
      'snapshotsRetention.firstOfMonth.enabled',
      firstOfMonth.enabled,
      setValueOptions,
    );
    setValue(
      'snapshotsRetention.firstOfMonth.months',
      String(firstOfMonth.months),
      setValueOptions,
    );
    setValue(
      'snapshotsRetention.firstOfYear.enabled',
      firstOfYear.enabled,
      setValueOptions,
    );
    setValue(
      'snapshotsRetention.firstOfYear.years',
      String(firstOfYear.years),
      setValueOptions,
    );
  });

  const handleDeletionRulesResetToDefaults = useStableCallback(() => {
    const {
      snapshotsRetention: { removalMetrics },
    } = prepareSnapshotCollectionValues();

    setValue(
      'snapshotsRetention.removalMetrics.hddUtilization',
      String(removalMetrics.hddUtilization),
      setValueOptions,
    );
    setValue(
      'snapshotsRetention.removalMetrics.snapshotsCount',
      String(removalMetrics.snapshotsCount),
      setValueOptions,
    );
  });

  // Triggers cron validation to show field-level errors on invalid cron fields
  const triggerCronValidation = useStableCallback(() => {
    if (isSnapshotsEnabled) {
      return cronRef.current?.triggerValidation();
    }
    return true;
  });

  const handleFormSubmit = useStableCallback(
    async (values: SnapshotCollectionFormValues) => {
      const cronIsValid = await triggerCronValidation();
      if (cronIsValid === false) {
        return;
      }

      const payload: SnapshotCollectionValues = {
        snapshots: {
          enabled: values.snapshots.enabled,
          cron: values.snapshots.cron,
          maxLoaded: Number(values.snapshots.maxLoaded),
          maxLocked: Number(values.snapshots.maxLocked),
        },
        snapshotsRetention: {
          all: {
            enabled: values.snapshotsRetention.all.enabled,
            days: Number(values.snapshotsRetention.all.days),
          },
          firstOfDay: {
            enabled: values.snapshotsRetention.firstOfDay.enabled,
            days: Number(values.snapshotsRetention.firstOfDay.days),
          },
          firstOfWeek: {
            enabled: values.snapshotsRetention.firstOfWeek.enabled,
            weeks: Number(values.snapshotsRetention.firstOfWeek.weeks),
          },
          firstOfMonth: {
            enabled: values.snapshotsRetention.firstOfMonth.enabled,
            months: Number(values.snapshotsRetention.firstOfMonth.months),
          },
          firstOfYear: {
            enabled: values.snapshotsRetention.firstOfYear.enabled,
            years: Number(values.snapshotsRetention.firstOfYear.years),
          },
          removalMetrics: {
            hddUtilization: Number(
              values.snapshotsRetention.removalMetrics.hddUtilization,
            ),
            snapshotsCount: Number(
              values.snapshotsRetention.removalMetrics.snapshotsCount,
            ),
          },
          action: retentionAction,
        },
      };
      onSubmit(payload);
    },
  );

  useEffect(() => {
    onFormStateChange?.({ isDirty, cronValid });
  }, [isDirty, cronValid, onFormStateChange]);

  useEffect(() => {
    if (!isSnapshotsEnabled) {
      cronRef.current?.resetValidation();
    }
  }, [isSnapshotsEnabled]);

  return (
    <FormProvider {...formMethods}>
      <form
        id={snapshotCollectionFormId}
        className='flex flex-col gap'
        onSubmit={handleSubmit(handleFormSubmit, triggerCronValidation)}
      >
        <LeaveDialog dirty={isDirty} />
        <Card className='p flex flex-col gap'>
          <div className='text-level-5 font-semibold'>{messages.boxTitle}</div>
          <div className='flex flex-col gap-sm'>
            <FormSwitch<SnapshotCollectionFormValues>
              control={control}
              name='snapshots.enabled'
              label={messages.enabled}
              disabled={submitting}
              className='w-fit'
            />
            <div className='flex flex-col gap-sm'>
              <FormCron<SnapshotCollectionFormValues>
                ref={cronRef}
                control={control}
                name='snapshots.cron'
                label={messages.interval}
                disabled={isCronDisabled}
                onMetaChange={handleCronMetaChange}
              />
            </div>
          </div>
        </Card>

        <Card className='p flex flex-col gap'>
          <div className='flex flex-row items-center gap-xs'>
            <div className='text-level-5 font-semibold'>
              {messages.retention.title}
            </div>
            <Link
              renderAs='button'
              onClick={handleCollectionRulesResetToDefaults}
              disabled={submitting}
            >
              {messages.retention.btnRestore}
            </Link>
          </div>
          <div className='flex flex-col gap'>
            <FormTextField<SnapshotCollectionFormValues>
              type='number'
              control={control}
              name='snapshots.maxLoaded'
              label={messages.maxLoaded}
              min={MAX_LOADED_SNAPSHOTS_FROM}
              max={MAX_LOADED_SNAPSHOTS_TO}
              step={1}
              primaryHelperText={`The maximum limit is ${MAX_LOADED_SNAPSHOTS_TO}. Increasing this limit may impact application performance.`}
              disabled={submitting}
              inputWidthClassName='w-72'
            />
            <FormTextField<SnapshotCollectionFormValues>
              type='number'
              control={control}
              name='snapshots.maxLocked'
              label={messages.maxLocked}
              min={MAX_LOCKED_SNAPSHOTS_FROM}
              max={Number(snapshotMaxLoaded) - 1}
              step={1}
              disabled={submitting}
              inputWidthClassName='w-72'
            />
          </div>
          <div className='flex flex-col gap'>
            <RetentionField
              control={control}
              enabledName='snapshotsRetention.all.enabled'
              valueName='snapshotsRetention.all.days'
              label={messages.retention.all.label}
              unit={messages.retention.all.unit}
              disabled={submitting}
            />
            <RetentionField
              control={control}
              enabledName='snapshotsRetention.firstOfDay.enabled'
              valueName='snapshotsRetention.firstOfDay.days'
              label={messages.retention.firstOfDay.label}
              unit={messages.retention.firstOfDay.unit}
              disabled={submitting}
            />
            <RetentionField
              control={control}
              enabledName='snapshotsRetention.firstOfWeek.enabled'
              valueName='snapshotsRetention.firstOfWeek.weeks'
              label={messages.retention.firstOfWeek.label}
              unit={messages.retention.firstOfWeek.unit}
              disabled={submitting}
            />
            <RetentionField
              control={control}
              enabledName='snapshotsRetention.firstOfMonth.enabled'
              valueName='snapshotsRetention.firstOfMonth.months'
              label={messages.retention.firstOfMonth.label}
              unit={messages.retention.firstOfMonth.unit}
              disabled={submitting}
            />
            <RetentionField
              control={control}
              enabledName='snapshotsRetention.firstOfYear.enabled'
              valueName='snapshotsRetention.firstOfYear.years'
              label={messages.retention.firstOfYear.label}
              unit={messages.retention.firstOfYear.unit}
              disabled={submitting}
            />
          </div>
        </Card>

        <Card className='p flex flex-col gap'>
          <div className='flex flex-col gap-2'>
            <div className='flex flex-row items-center gap-xs'>
              <div className='text-level-5 font-semibold'>
                {messages.retention.removalConditionsTitle}
              </div>
              <Link
                renderAs='button'
                onClick={handleDeletionRulesResetToDefaults}
                disabled={submitting}
              >
                {messages.retention.btnRestore}
              </Link>
            </div>
            <p className='text-sm'>
              {messages.retention.removalConditionsExplanation}
            </p>
          </div>
          <div className='flex flex-col gap'>
            <FormTextField<SnapshotCollectionFormValues>
              control={control}
              name='snapshotsRetention.removalMetrics.hddUtilization'
              label={messages.retention.hddUtilization.label}
              type='number'
              disabled={submitting}
              inputWidthClassName='w-60'
              endAdornment={messages.retention.hddUtilization.unit}
              min={1}
            />

            <FormTextField<SnapshotCollectionFormValues>
              control={control}
              name='snapshotsRetention.removalMetrics.snapshotsCount'
              label={messages.retention.snapshotsCount.label}
              type='number'
              disabled={submitting}
              inputWidthClassName='w-60'
              min={1}
            />
          </div>
        </Card>
      </form>
    </FormProvider>
  );
};
